<template>
  <v-dialog
    v-model="dialogValue"
    persistent
    max-width="1200"
  >
    <v-card class="px-4 py-12">
      <div class="close-button-container">
        <v-btn
          v-if="showCloseButton"
          icon="mdi-close"
          size="large"
          variant="plain"
          color="grey-darken-1"
          @click="close"
        />
      </div>

      <v-card-text class="pa-0">
        <v-expand-transition>
          <!-- Step 1 - Products -->
          <div
            v-if="currentStep === 1"
            key="step1"
          >
            <div class="text-center mb-4">
              <h1 class="text-h4 font-weight-black mb-2">{{ $t('products.title') }}</h1>
              <p class="text-subtitle-2">{{ $t('products.subtitle') }}</p>
            </div>

            <Products
              :country-code="company.country.toUpperCase()"
              :is-package-change="true"
              :hide-downgrade="hideDowngrade"
              :current-package="currentPackage"
              @productSelected="handleProductSelected"
            />
          </div>

          <!-- Step 2 - Warnings -->
          <div
            v-else-if="currentStep === 2"
            key="step2"
            class="warning-container"
          >
            <div class="warning-content">
              <UpgradeWarning
                v-if="isUpgrade"
                :current-package="currentPackage"
                :new-package="newPackage"
                :is-loading="isLoading"
                @cancel="handleCancel"
                @submit="changePackage"
              />

              <DowngradeWarning
                v-else
                :current-package="currentPackage"
                :new-package="newPackage"
                :is-loading="isLoading"
                @cancel="handleCancel"
                @submit="changePackage"
              />
            </div>
          </div>

          <!-- Step 3 - Change Animation -->
          <div
            v-else-if="currentStep === 3"
            key="step3"
            class="content-container"
          >
            <ChangeAnimation
              :current-package="currentPackage"
              :new-package="newPackage"
              :is-change-subscription-done="isChangeSubscriptionDone"
              @finished="handleLoadingFinished"
            />
          </div>

          <!-- Step 4 - Upgrade Onboarding -->
          <div
            v-else-if="currentStep === 4"
            key="step4"
            class="content-container"
          >
            <UpgradeOnboarding
              v-if="isUpgrade"
              :company="company"
              :new-package="newPackage"
              @submit="handleNextStep"
              @scrollToTop="scrollToTop"
            />
          </div>

          <!-- Step 5 - Finished Animation -->
          <div
            v-else-if="currentStep === 5"
            key="step5"
            class="content-container"
          >
            <FinishedAnimation
              :is-upgrade="isUpgrade"
              :new-package="newPackage"
              @finished="handleUpgradeOnboardingFinished"
            />
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Products from '@/components/Products'
import bus, { eventNames } from '@/lib/eventBus'
import { isUpgrade } from '@/lib/product'
import { ProductType } from '@/lib/productTypes'
import IDENTITY from '@/queries/Identity.gql'

import ChangeAnimation from './ChangeAnimation.vue'
import DowngradeWarning from './DowngradeWarning.vue'
import FinishedAnimation from './FinishedAnimation.vue'
import DOWNGRADE_TO_BASIC from './queries/DowngradeToBasic.gql'
import DOWNGRADE_TO_LIGHT from './queries/DowngradeToLight.gql'
import UPGRADE_TO_BASIC from './queries/UpgradeToBasic.gql'
import UPGRADE_TO_EXPERT from './queries/UpgradeToExpert.gql'
import UpgradeOnboarding from './UpgradeOnboarding.vue'
import UpgradeWarning from './UpgradeWarning.vue'

export default {
  components: {
    Products,
    ChangeAnimation,
    UpgradeOnboarding,
    FinishedAnimation,
    DowngradeWarning,
    UpgradeWarning,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      default: () => ({}),
    },
    hideDowngrade: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:dialog', 'close'],
  data() {
    return {
      isLoading: false,
      currentStep: 1,
      newPackage: '',
      isYearly: true,
      isUpgrade: true,
      isChangeSubscriptionDone: false,
    }
  },
  computed: {
    dialogValue: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    showCloseButton() {
      return this.currentStep === 1
    },
    currentPackage() {
      return this.company?.productType
    },
  },
  watch: {
    currentStep() {
      this.$nextTick(() => {
        this.scrollToTop()
      })
    },
  },
  methods: {
    scrollToTop() {
      const dialogContent = document.querySelector('.v-overlay__content')
      if (dialogContent) {
        dialogContent.scrollTop = 0
      }
    },
    handleLoadingFinished() {
      if (this.isUpgrade) {
        this.handleNextStep()
      } else {
        this.handleNextStep(2)
      }
    },
    handleCancel() {
      this.currentStep = 1
    },
    handleGoToFirstStep() {
      this.currentStep = 1
    },
    handleNextStep(value = 1) {
      const nextStep = this.currentStep + value
      this.currentStep = nextStep < 1 ? 1 : nextStep
    },
    async changePackage() {
      await this.handlePackageChange({ productName: this.newPackage })
      this.handleNextStep()
    },
    async handleProductSelected({ productName, isYearly }) {
      this.newPackage = productName
      this.isUpgrade = isUpgrade(this.currentPackage, this.newPackage)
      this.isYearly = isYearly
      this.currentStep = 2
      this.$tracking.event('Plans and Pricing', 'Clicked', productName)
    },
    async handlePackageChange({ productName }) {
      const mutation = {
        [ProductType.LIGHT]: DOWNGRADE_TO_LIGHT,
        [ProductType.BASIC]: this.isUpgrade ? UPGRADE_TO_BASIC : DOWNGRADE_TO_BASIC,
        [ProductType.EXPERT]: UPGRADE_TO_EXPERT,
      }[productName]

      try {
        this.isLoading = true
        await this.$apollo.mutate({
          mutation,
          variables: {
            input: {
              isYearly: this.isYearly,
              companyId: this.$auth.user.companyId,
            },
          },
        })

        await this.$auth.getTokenSilently({
          ignoreCache: true,
        })
        await this.$apollo.query({ query: IDENTITY })
        await this.$features.loadAllFeaturesForCompany(this.$auth.user.companyId, { reload: true })

        this.$tracking.event('Settings', 'Clicked', 'Switch Package')

        this.isChangeSubscriptionDone = true
      } catch (e) {
        if (e.message.match(/Cannot change from yearly to monthly/)) {
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'error',
            text: 'Wechsel von jährlich auf monatlich nicht möglich.',
          })
        } else {
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'error',
            text: 'Problem beim wechseln des Pakets.',
          })
        }
        this.handleGoToFirstStep()
      } finally {
        this.isLoading = false
      }
    },
    handleUpgradeOnboardingFinished() {
      this.$router.go(0)
    },
    close() {
      this.currentStep = 1
      this.isChangeSubscriptionDone = false
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.close-button-container {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
}

.warning-container,
.content-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.warning-content {
  width: 600px;
  max-width: 100%;
}

:deep(.v-card-text) {
  overflow: visible !important;
}

/* Responsive widths for different steps */
@media (min-width: 600px) {
  .content-container > * {
    width: 800px;
    max-width: 100%;
  }
}
</style>
